import { ArrowRight } from "lucide-react";
import KairosisLogo from "@/components/KairosisLogo";
import CompanyFooter from "@/components/CompanyFooter";
import { COMPANY, DISCLAIMER, addressLine } from "@/lib/company";

function Row({ label, children }) {
  return (
    <div className="flex flex-col gap-1 border-b border-white/[0.06] py-3.5 sm:flex-row sm:gap-6">
      <dt className="w-48 shrink-0 text-sm text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-200">{children}</dd>
    </div>
  );
}

// The company and risk page, shared by every product on the site.
export default function LegalPage() {
  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans overflow-x-hidden">
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 kairosis-aurora" />
        <div className="absolute inset-0 kairosis-noise" />
      </div>

      <nav className="relative z-50 flex items-center justify-between px-4 sm:px-6 lg:px-8 py-4 border-b border-white/[0.06] backdrop-blur-xl bg-black/40">
        <a href="/" className="hover:opacity-90 transition-opacity"><KairosisLogo size="md" /></a>
        <a href="/" className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
          Back to site <ArrowRight className="w-4 h-4" />
        </a>
      </nav>

      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-[34px] font-semibold tracking-tight">Legal &amp; risk</h1>

        <h2 className="mt-12 text-lg font-semibold">Company</h2>
        <dl className="mt-4">
          <Row label="Legal name">{COMPANY.legalName}</Row>
          <Row label="Legal status">{COMPANY.legalStatus}</Row>
          <Row label="Licence number">{COMPANY.licenceNumber}</Row>
          <Row label="Issuing authority">{COMPANY.issuingAuthority}</Row>
          <Row label="Registered address">{addressLine()}</Row>
          <Row label="Contact">
            <a className="underline underline-offset-4 hover:text-white" href={`mailto:${COMPANY.supportEmail}`}>
              {COMPANY.supportEmail}
            </a>
          </Row>
        </dl>

        <h2 className="mt-12 text-lg font-semibold">Licensed activities</h2>
        <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-6 text-gray-400">
          {COMPANY.activities.map((a) => <li key={a}>{a}</li>)}
        </ul>
        {/* Stated rather than implied. The licence is for software; a reader who
            assumes otherwise has been misled by omission. */}
        <p className="mt-4 text-sm leading-6 text-gray-400">
          These are software and consultancy activities. We are not licensed as a broker, an
          investment adviser, or an asset manager, and do not act as one.
        </p>

        <h2 className="mt-12 text-lg font-semibold">Risk disclosure</h2>
        <p className="mt-4 text-sm leading-6 text-gray-400">{DISCLAIMER}</p>
        <p className="mt-4 text-sm leading-6 text-gray-400">
          You connect your own broker account and you keep control of it. Orders are placed only when
          a level you saved is reached and you have switched that level to run automatically. Every
          order passes a deterministic risk check first, and you can stop all automation at any time.
          Your broker, not us, holds your funds and executes your orders.
        </p>
      </main>

      <CompanyFooter />
    </div>
  );
}
